import { ArrowIcon } from '@/components/icons/arrow'
import { routes } from '@/config/routes'
import type { VaultApyComposite, VaultApyRange } from '@/types/apy'
import type { VaultRewards } from '@/types/rewards'
import { getApyCopy, getApyRangeCopy, getApyValue } from '@/utils/apy'
import { cn } from '@/utils/cn'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getValueColor } from '@/utils/styles/getValueColor'
import { formatDecimalToPercentage, formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import { Fragment, type ReactNode, useMemo } from 'react'
import { Link, generatePath } from 'react-router'
import type { Address } from 'viem'
import { ApyTooltip } from '../common/apyTooltip'
import { BlurCover } from '../common/blurCover'
import { Card } from '../common/card'
import { NetworkImage } from '../common/networkImage'
import { ProtocolImage } from '../common/protocolImage'
import { ScrollArea, ScrollBar } from '../common/scrollArea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableRowLink } from '../common/table'
import { TotalReturns } from '../shared/totalReturns'
import type { BreakdownTab } from './activePositionsChart'
import { calculateAverageApy } from './useGroupedBalances/calculateAverageApy'
import { groupBalances } from './useGroupedBalances/groupBalances'
import type { LpTokensBalanceGroup } from './useGroupedBalances/useGroupedPositions'

interface SharedProps {
  walletAddress: Address | undefined
  includeRewards: boolean
  apyRange: VaultApyRange
}

interface Props {
  lpTokensBalance: LpTokensBalanceGroup
  breakdownTab: BreakdownTab
  className?: string
  loading?: boolean
}

type Position = LpTokensBalanceGroup[number]['balances'][number]

interface PositionGroup {
  groupName: string
  groupDisplayName: string
  balances: Position[]
  groupTotalBalance: number
  groupApy: number
}

function useRegroupedPositions(
  lpTokensBalance: LpTokensBalanceGroup,
  tab: BreakdownTab,
  includeRewards: boolean
): PositionGroup[] {
  return useMemo(() => {
    const computeGroupApy = (balances: Position[]) =>
      calculateAverageApy(
        balances.map(({ balanceUsd, apy }) => ({
          balanceUsd,
          apy: includeRewards ? apy.total : apy.base,
        }))
      )
    if (tab === 'asset') {
      return lpTokensBalance.map(({ groupName, balances, groupTotalBalance, groupApy }) => ({
        groupName,
        groupDisplayName: groupName,
        balances,
        groupTotalBalance,
        groupApy,
      }))
    }
    const flat = lpTokensBalance.flatMap((group) => group.balances)
    const getKey = tab === 'protocol' ? (p: Position) => p.protocol.name : (p: Position) => p.network.name
    return groupBalances(flat, getKey).map((group) => ({
      ...group,
      groupDisplayName:
        tab === 'protocol'
          ? getProtocolDisplayName(group.balances[0]?.protocol ?? { name: group.groupName })
          : group.groupName,
      groupApy: computeGroupApy(group.balances),
    }))
  }, [lpTokensBalance, tab, includeRewards])
}

const GroupLabel = ({
  groupName,
  groupDisplayName,
  tab,
}: {
  groupName: string
  groupDisplayName: string
  tab: BreakdownTab
}) => {
  if (tab === 'protocol') {
    return (
      <span className={cn('flex items-center gap-2')}>
        <ProtocolImage protocol={{ name: groupName, displayName: groupDisplayName }} className="size-5 p-0.5" />
        <span className="font-medium">{groupDisplayName}</span>
      </span>
    )
  }
  if (tab === 'network') {
    return (
      <span className="flex items-center gap-2">
        <NetworkImage network={groupName} className="size-5 p-0.5" />
        <span className="font-medium capitalize">{getNetworkName(groupName)}</span>
      </span>
    )
  }
  return <span className="font-medium uppercase">{groupName}</span>
}

const GroupHeaderRow = ({ group, tab }: { group: PositionGroup; tab: BreakdownTab }) => {
  return (
    <div className="flex items-center justify-between gap-4">
      <GroupLabel groupName={group.groupName} groupDisplayName={group.groupDisplayName} tab={tab} />
      <span className="flex items-center gap-2">
        <span className={cn('tabular-nums', getValueColor(group.groupApy))}>
          {formatDecimalToPercentage(group.groupApy)}
        </span>{' '}
        (avg.)
      </span>
    </div>
  )
}

export function ActivePositionsPanel({
  lpTokensBalance,
  walletAddress,
  includeRewards,
  apyRange,
  breakdownTab,
  loading,
}: Props & SharedProps) {
  const groups = useRegroupedPositions(lpTokensBalance, breakdownTab, includeRewards)

  return (
    <Card as="section" className="relative h-fit overflow-hidden p-0">
      <ScrollArea type="auto" className="w-full">
        <ScrollBar orientation="horizontal" className="top-0" />
        <Table className="min-w-[640px]">
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Protocol</TableHead>
              <TableHead className="text-right">Position value</TableHead>
              <TableHead className="text-right">Total returns</TableHead>
              <TableHead className="text-right">Yield ({getApyRangeCopy(apyRange).compact})</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody className={cn(loading && 'animate-pulse')}>
            {groups.length > 0 ? (
              groups.map((group) => (
                <Fragment key={group.groupName}>
                  <tr>
                    <td colSpan={5} className="border-y-2 bg-surface px-3 py-1.5">
                      <GroupHeaderRow group={group} tab={breakdownTab} />
                    </td>
                  </tr>
                  {group.balances.map((position) => (
                    <PositionRow
                      key={`${position.network.name}:${position.vaultId}`}
                      position={position}
                      walletAddress={walletAddress}
                      includeRewards={includeRewards}
                      apyRange={apyRange}
                    />
                  ))}
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="py-5 text-center">
                  <p className="font-semibold text-base">No active positions</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {loading && <BlurCover className="bg-transparent" />}
    </Card>
  )
}

function buildApyData(position: Position, apyRange: VaultApyRange, includeRewards: boolean) {
  const { asset, apy, apyComposite } = position
  const apyByRange = { '1day': apy, '7day': apy, '30day': apy }
  const positionRewards: VaultRewards =
    apy.reward !== 0 ? [{ asset, apy: { '1day': apy.reward, '7day': apy.reward, '30day': apy.reward } }] : []
  const apyCompositeByRange: VaultApyComposite | undefined = apyComposite
    ? {
        totalApy: {
          '1hour': apyComposite.totalApy,
          '1day': apyComposite.totalApy,
          '7day': apyComposite.totalApy,
          '30day': apyComposite.totalApy,
        },
        intrinsicApy: {
          '1hour': apyComposite.intrinsicApy,
          '1day': apyComposite.intrinsicApy,
          '7day': apyComposite.intrinsicApy,
          '30day': apyComposite.intrinsicApy,
        },
        asset: apyComposite.asset,
        vault: apyComposite.vault,
      }
    : undefined
  const apyValue = getApyValue({ apy: apyByRange, apyRange, includeRewards, apyComposite: apyCompositeByRange })
  return { apyByRange, positionRewards, apyCompositeByRange, apyValue }
}

interface PositionRowProps {
  position: Position
}

const PositionRow = ({ position, walletAddress, includeRewards, apyRange }: PositionRowProps & SharedProps) => {
  const { network, protocol, name, asset, balanceNative, balanceUsd, apy } = position
  const { apyByRange, positionRewards, apyCompositeByRange, apyValue } = buildApyData(
    position,
    apyRange,
    includeRewards
  )
  return (
    <TableRow
      data-row="position"
      data-highlight={`${network.name}:${position.vaultId}`}
      className="relative transition-colors even:bg-page [[data-row=position]+&>td]:border-t"
    >
      <TableCell className="h-18 max-w-32">
        <span data-highlight-frame aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl" />
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <ProtocolImage protocol={protocol} className="size-8 p-1" />
            <NetworkImage network={network.name} className="absolute right-0 bottom-0 size-4 p-px" />
          </div>
          <div className="min-w-0">
            <Link
              to={generatePath(routes.opportunity, { vaultId: position.vaultId, network: network.name })}
              className="block truncate font-medium hocus:text-accent text-heading-foreground text-sm outline-none transition-colors before:absolute before:inset-0"
            >
              {name}
            </Link>
            <p className="text-body-foreground text-xs">
              on <span className="capitalize">{getNetworkName(network.name)}</span>
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <p className="font-medium text-heading-foreground text-sm tabular-nums">
          {formatPreciseTokenValue({ value: balanceNative, decimals: asset.decimals })} {asset.symbol}
        </p>
        <p className="text-body-foreground text-xs tabular-nums">≈ {formatUSD(balanceUsd)}</p>
      </TableCell>
      <TableCell className="text-right">
        <TotalReturns
          vault={position}
          walletAddress={walletAddress}
          containRewards={Boolean(apy.reward)}
          colorize
          inlineRewards
          className="flex flex-col items-end"
          primaryValueClassName="font-medium text-sm tabular-nums"
          secondaryValueClassName="text-xs text-body-foreground tabular-nums"
          subscriptionClassName="text-xs text-body-foreground"
        />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <ApyTooltip
            asset={asset}
            apy={apyByRange}
            apyRange={apyRange}
            rewards={positionRewards}
            apyComposite={apyCompositeByRange}
            includeRewards={includeRewards}
            className="relative z-[1]"
          />
          <p className={cn('font-medium text-sm tabular-nums', getValueColor(apyValue))}>
            {getApyCopy({ apy: apyByRange, apyRange, includeRewards, apyComposite: apyCompositeByRange })}
          </p>
        </div>
      </TableCell>
      <TableCell className="!pl-1 !pr-3 w-10">
        <TableRowLink
          to={generatePath(routes.opportunity, { vaultId: position.vaultId, network: network.name })}
          title={name}
          prefetch="intent"
          className="focus-visible:before:rounded-xl focus-visible:before:ring focus-visible:before:ring-inset"
        >
          <ArrowIcon />
        </TableRowLink>
      </TableCell>
    </TableRow>
  )
}

export function ActivePositionsContent({
  lpTokensBalance,
  walletAddress,
  includeRewards,
  apyRange,
  breakdownTab,
  className,
}: Props & SharedProps) {
  const groups = useRegroupedPositions(lpTokensBalance, breakdownTab, includeRewards)

  return (
    <div className={className}>
      {groups.length > 0 ? (
        groups.map((group) => (
          <div key={group.groupName}>
            <div className="border-y bg-surface px-4 py-1.5 text-body-foreground text-xs md:px-5">
              <GroupHeaderRow group={group} tab={breakdownTab} />
            </div>
            <ul className="flex flex-col gap-2 p-4 md:px-5">
              {group.balances.map((position) => (
                <li key={`${position.network.name}:${position.vaultId}`}>
                  <PositionCard
                    position={position}
                    walletAddress={walletAddress}
                    includeRewards={includeRewards}
                    apyRange={apyRange}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        <div className="flex items-center justify-center py-5">
          <p className="font-semibold">No active positions</p>
        </div>
      )}
    </div>
  )
}

function StatCell({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex xs:flex-col xs:justify-normal justify-between gap-2 xs:gap-0.5', className)}>
      <p className="text-body-foreground text-xs">{label}</p>
      {children}
    </div>
  )
}

interface PositionCardProps {
  position: Position
}

function PositionCard({ position, walletAddress, includeRewards, apyRange }: PositionCardProps & SharedProps) {
  const { network, protocol, name, asset, balanceNative, balanceUsd, apy } = position
  const { apyByRange, positionRewards, apyCompositeByRange, apyValue } = buildApyData(
    position,
    apyRange,
    includeRewards
  )

  return (
    <Card
      data-highlight={`${network.name}:${position.vaultId}`}
      className="relative flex flex-col gap-4 rounded-2xl p-4 transition-colors"
    >
      <span data-highlight-frame aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl" />
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <ProtocolImage protocol={protocol} className="size-10 p-1" />
          <NetworkImage network={network.name} className="absolute right-0 bottom-0 size-5 p-px" />
        </div>
        <div className="min-w-0 flex-1">
          <Link
            to={generatePath(routes.opportunity, { vaultId: position.vaultId, network: network.name })}
            className="block truncate font-medium hocus:text-accent text-heading-foreground text-sm outline-none transition-colors before:absolute before:inset-0"
          >
            {name}
          </Link>
          <p className="text-body-foreground text-xs">
            on <span className="capitalize">{getNetworkName(network.name)}</span>
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 xs:grid-cols-3 gap-2 xs:gap-3">
        <StatCell label="Position value">
          <div className="flex flex-col xs:items-start items-end">
            <p className="font-medium text-heading-foreground text-sm tabular-nums">
              {formatPreciseTokenValue({ value: balanceNative, decimals: asset.decimals })} {asset.symbol}
            </p>
            <p className="text-body-foreground text-xs tabular-nums">≈ {formatUSD(balanceUsd)}</p>
          </div>
        </StatCell>
        <StatCell label="Earned so far" className="xs:mx-auto xs:w-fit">
          <TotalReturns
            vault={position}
            walletAddress={walletAddress}
            containRewards={Boolean(apy.reward)}
            colorize
            inlineRewards
            className="flex flex-col xs:items-start items-end"
            primaryValueClassName="font-medium text-sm tabular-nums"
            secondaryValueClassName="text-xs text-body-foreground tabular-nums"
            subscriptionClassName="text-xs text-body-foreground"
          />
        </StatCell>
        <StatCell label="Earning rate" className="xs:items-end">
          <div className="flex items-center gap-1">
            <p className={cn('font-medium text-sm tabular-nums', getValueColor(apyValue))}>
              {getApyCopy({ apy: apyByRange, apyRange, includeRewards, apyComposite: apyCompositeByRange })}
            </p>
            <ApyTooltip
              asset={asset}
              apy={apyByRange}
              apyRange={apyRange}
              rewards={positionRewards}
              apyComposite={apyCompositeByRange}
              includeRewards={includeRewards}
              className="relative z-[1]"
            />
          </div>
        </StatCell>
      </div>
    </Card>
  )
}
